/**
 * PHARMAKON — `AlerteHost` : l'HÔTE d'affichage des alertes équipe.
 *
 * PLAN_mobile_pharma_owner_manager §3.5.
 *
 * Monté UNE FOIS dans `components/layout/AppShell.jsx`, à côté de `ToastHost`.
 * Il tient trois choses :
 *
 *   1. LE CYCLE DE VIE du sondage (le chemin de correction) et du flux SSE
 *      (l'optimisation de latence) — voir `stores/alerteStore.js` ;
 *   2. LA SOUPAPE DE SÉCURITÉ DU COMPTOIR — la surcouche bloquante ne se monte
 *      jamais pendant une mutation d'encaissement en vol, et se monte TOUJOURS
 *      quand même au plus tard après `max_defer_ms` (§3.5) ;
 *   3. LE RENDU des paliers « important » (toast persistant) et « URGENT »
 *      (surcouche bloquante). Le palier « info » est rendu par
 *      `<AlerteBannieresSlot/>`, monté DANS `<main>` pour défiler avec le
 *      contenu.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LES DEUX MOITIÉS DE LA SOUPAPE, toutes deux obligatoires (§3.5) :
 *   (a) elle NE SE MONTE JAMAIS pendant un `POST /ventes/…` ou un mouvement de
 *       trésorerie — voler le focus à cet instant est un incident de caisse ;
 *   (b) elle FINIT TOUJOURS par se monter — sinon garder une vente ouverte
 *       ferait taire une alerte urgente indéfiniment.
 * Le test E2E de la journée B assert les DEUX.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import * as React from "react";
import apiClient from "@/services/api.client.js";
import useAlerteStore, { repartir } from "@/stores/alerteStore.js";
import { useAuthStore } from "@/stores/authStore.js";
import AlerteBannieres from "./AlerteBannieres.jsx";
import AlerteToastPersistant from "./AlerteToastPersistant.jsx";
import AlerteBloquante from "./AlerteBloquante.jsx";
import AlerteConsole from "./AlerteConsole.jsx";
import {
  installerCapteurPos,
  surSectionCritique,
  sectionCritiqueOccupee,
  decisionMontage,
} from "./criticalSection.js";
import { peutRecevoirAlertes } from "./alerte-equipe.config.js";
import "./alertes.css";

/** Les rôles qui reçoivent un flux — la garde d'interface, le serveur décide. */
function useDestinataire() {
  const user = useAuthStore((s) => s.user);
  return peutRecevoirAlertes(user);
}

/**
 * Sélecteur de répartition. Recalculé à chaque changement de `alertes` ou de la
 * limite de bandeaux — jamais dans le rendu d'un enfant, pour que les trois
 * files soient cohérentes entre elles sur une même image.
 */
function useFiles() {
  const alertes = useAlerteStore((s) => s.alertes);
  const maxBannieres = useAlerteStore((s) => s.cadence.maxBannieres);
  return React.useMemo(() => repartir(alertes, maxBannieres), [alertes, maxBannieres]);
}

// ═════════════════════════════════════════════════════════════════════════════
// La fente « info » — montée DANS <main>, sous LicenceBanner
// ═════════════════════════════════════════════════════════════════════════════

export function AlerteBannieresSlot() {
  const destinataire = useDestinataire();
  const { bannieres, bannieresEnTrop } = useFiles();
  const marquerVu = useAlerteStore((s) => s.marquerVu);
  if (!destinataire) return null;
  return (
    <AlerteBannieres
      alertes={bannieres}
      enTrop={bannieresEnTrop}
      onFermer={marquerVu}
    />
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// L'hôte — cycle de vie + toasts + surcouche bloquante
// ═════════════════════════════════════════════════════════════════════════════

export default function AlerteHost() {
  const destinataire = useDestinataire();
  const demarrer = useAlerteStore((s) => s.demarrer);
  const arreter = useAlerteStore((s) => s.arreter);
  const sonderMaintenant = useAlerteStore((s) => s.sonderMaintenant);
  const marquerVu = useAlerteStore((s) => s.marquerVu);
  const accuser = useAlerteStore((s) => s.accuser);
  const maxDeferMs = useAlerteStore((s) => s.cadence.maxDeferMs);
  const degrade = useAlerteStore((s) => s.degrade);
  const { toasts, bloquantes } = useFiles();

  // ── Le capteur POS : posé une seule fois, sur le client HTTP partagé ───────
  React.useEffect(() => {
    installerCapteurPos(apiClient);
  }, []);

  // ── Le sondage ────────────────────────────────────────────────────────────
  React.useEffect(() => {
    if (!destinataire) return undefined;
    demarrer();
    return () => arreter();
  }, [destinataire, demarrer, arreter]);

  // Retour d'onglet : on sonde TOUT DE SUITE plutôt que d'attendre la fin du
  // cycle lent — c'est le « immediate poll on visibilitychange » du §3.4.
  React.useEffect(() => {
    if (!destinataire) return undefined;
    const onVisibilite = () => {
      if (document.visibilityState === "visible") sonderMaintenant();
    };
    document.addEventListener("visibilitychange", onVisibilite);
    window.addEventListener("online", sonderMaintenant);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilite);
      window.removeEventListener("online", sonderMaintenant);
    };
  }, [destinataire, sonderMaintenant]);

  // ── La soupape de sécurité du comptoir ────────────────────────────────────
  //
  // La file bloquante s'affiche UNE À LA FOIS, la plus ancienne d'abord — le
  // tri vient du serveur, on prend la tête.
  const courante = bloquantes[0] ?? null;
  const couranteId = courante?.id ?? null;

  const [occupe, setOccupe] = React.useState(() => sectionCritiqueOccupee());
  /**
   * L'identifiant de l'alerte dont le report a EXPIRÉ, pas un booléen.
   *
   * Un booléen devrait être remis à zéro quand la file se vide ou change
   * d'alerte — c'est-à-dire un `setState` dans un effet, donc un rendu en
   * cascade. Mémoriser l'identifiant rend la remise à zéro IMPLICITE : dès que
   * l'alerte courante change, la comparaison est fausse d'elle-même, et la
   * suivante repart avec son plein droit de report.
   */
  const [forcePour, setForcePour] = React.useState(null);
  /** { id, depuis } — quand la mise en file de CETTE alerte a commencé. */
  const file = React.useRef({ id: null, depuis: null });

  React.useEffect(() => surSectionCritique(({ occupe: o }) => setOccupe(o)), []);

  React.useEffect(() => {
    if (!couranteId) {
      file.current = { id: null, depuis: null };
      return undefined;
    }
    if (file.current.id !== couranteId) {
      file.current = { id: couranteId, depuis: Date.now() };
    }
    // Comptoir au repos : le montage est DÉRIVÉ ci-dessous, immédiat, sans
    // passer par un état — c'est la moitié (a) du contrat, et le cas courant.
    if (!occupe) return undefined;

    const d = decisionMontage({
      occupe,
      enFileDepuis: file.current.depuis,
      maintenant: Date.now(),
      maxDeferMs,
    });

    // (b) — le plafond. Un seul chemin de réveil, le minuteur : quand le
    // plafond est DÉJÀ dépassé, `reste` vaut 0 et il tire au tour suivant.
    // Sans lui, une requête qui n'aboutit jamais scellerait la surcouche et
    // « garder une vente ouverte » ferait taire une alerte urgente pour
    // toujours. Avec lui, le report est borné, quoi qu'il arrive au réseau.
    const reste = Math.max(0, maxDeferMs - d.attenduMs);
    const t = setTimeout(() => {
      // Ce report est une information d'exploitation, pas un détail : s'il
      // devient fréquent, `max_defer_ms` est mal réglé ou une requête ne se
      // referme pas.
      console.warn(
        `[AlerteEquipe] surcouche URGENTE montée de force après ${maxDeferMs} ms ` +
          `de report — une mutation d'encaissement était en vol.`,
      );
      setForcePour(couranteId);
    }, reste + 1);
    return () => clearTimeout(t);
  }, [couranteId, occupe, maxDeferMs]);

  // LE contrat, en une ligne : au repos on monte tout de suite ; occupé on
  // attend, mais jamais au-delà du plafond.
  const montee = !!couranteId && (!occupe || forcePour === couranteId);

  if (!destinataire) return null;

  return (
    <>
      <AlerteToastPersistant alertes={toasts} onFermer={marquerVu} />

      {degrade && (
        // §3.4 — « un badge dégradé visible ». On ne prétend PAS que tout va
        // bien quand on ne sait plus si une alerte est arrivée.
        <div className="pk-alerte-degrade" role="status" data-testid="alerte-degrade">
          Alertes équipe : connexion instable — les alertes peuvent être en retard.
        </div>
      )}

      {courante && montee && (
        <AlerteBloquante
          key={courante.id}
          alerte={courante}
          index={0}
          total={bloquantes.length}
          onAccuser={accuser}
        />
      )}

      {/* La console de rédaction (Ctrl + Maj + A). Elle se garde toute seule :
          `peutRediger` exclut le caissier (Q3) et le support plateforme (§5.6). */}
      <AlerteConsole />
    </>
  );
}
